import { ref, computed, onUnmounted } from 'vue';

export function parseAiAnswer(content) {
  const { $t } = useNuxtApp();
  const result = {
    message: '',
    references: [],
  };

  try {
    if (!content) {
      return result;
    }

    const parsedData = JSON.parse(content);

    const answer = parsedData.answer?.trim() || '';
    result.message = answer || $t('search.aiAnswer.noAnswer');

    if (parsedData.references && Array.isArray(parsedData.references)) {
      result.references = parsedData.references
        .filter((ref) => ref.title && ref.url) // Only include valid references
        .map((ref) => ({
          title: ref.title.trim(),
          url: ref.url.trim(),
        }));
    }

    return result;
  } catch (error) {
    console.error('Error parsing AI answer JSON:', error);
    return result;
  }
}

export function useAiAnswer() {
  const { $t } = useNuxtApp();
  const message = ref('');
  const references = ref([]);
  const status = ref(''); // 'in_progress', 'complete', 'failed'
  const isLoading = ref(false);
  const id = ref(null);
  // Consolidated AI data object
  const aiData = computed(() => ({
    answer: message.value,
    references: references.value,
    status: status.value,
    isLoading: isLoading.value,
    id: id.value,
  }));

  let pollingInterval;
  const POLLING_INTERVAL = 1000; // Poll every 1 second

  const startPolling = async (chatId, sessionId) => {
    if (!chatId || !sessionId) {
      console.error('Chat ID and Session ID are required for AI answer polling');
      return;
    }
    stopPolling();
    id.value = chatId;
    isLoading.value = true;
    status.value = 'in_progress';

    const { $axios } = useNuxtApp();

    const pollChat = async () => {
      try {
        const response = await $axios.get(`api/v6/ai/chat/${chatId}`, {
          params: {
            session_id: sessionId,
          },
        });

        const chatData = response.data;
        status.value = chatData.status;

        if (chatData.status === 'complete') {
          const parsed = parseAiAnswer(chatData.last_message?.content);
          message.value = parsed.message;
          references.value = parsed.references;

          stopPolling();
        } else if (chatData.status === 'failed') {
          console.error('AI answer failed:', chatData.error || 'Unknown error');
          message.value = $t('search.aiAnswer.failed');

          stopPolling();
        } else if (chatData.status === 'in_progress') {
          pollingInterval = setTimeout(pollChat, POLLING_INTERVAL);
        }
      } catch (error) {
        console.error('Error polling AI answer:', error);
        message.value = $t('search.aiAnswer.failed');
        status.value = 'failed';

        stopPolling();
      }
    };

    await pollChat();
  };

  const stopPolling = () => {
    if (pollingInterval) {
      clearTimeout(pollingInterval);
      pollingInterval = null;
    }
    isLoading.value = false;
  };

  onUnmounted(() => {
    stopPolling();
  });

  return {
    aiData,
    startPolling,
    stopPolling,
  };
}
